import type { LineIndex } from '@parser';
import { combine, Lexer, Token, type StringToken, type TokenType } from '@parser';
import { Arrow } from './Arrow';
import * as TT from './CadetTokens';
import { BlockComment, LineComment } from './Comment';
import { JSONString } from './JSONString';
import { Prologue } from './Prologue';
import { SemVer } from './SemVer';

// ECMAScript identifier
const identifierRegex = /^[\p{ID_Start}$_][\p{ID_Continue}]*/u;

// Colon followed by ECMAScript identifier
const directiveRegex = /^:[\p{ID_Start}$_][\p{ID_Continue}]*/u;

// Slash followed by ECMAScript identifier
const classRegex = /^\/[\p{ID_Start}$_][\p{ID_Continue}]*/u;

export class CadetLexer extends Lexer {
  constructor(text: string, opts?: { lineIndex?: LineIndex }) {
    super(text, { lineIndex: opts?.lineIndex });

    if (!this.lexPrologue()) {
      return;
    }

    while (!this.isEOF) {
      this.skipWS();

      if (this.lexLineComment()) {
        continue;
      }
      if (this.lexBlockComment()) {
        continue;
      }
      if (this.lexBreak()) {
        continue;
      }
      if (this.lexNumber()) {
        continue;
      }
      // `true`, `false`, and `null` may not be used as identifiers
      if (this.lexMatch(/^true/, TT.TRUE)) {
        continue;
      }
      if (this.lexMatch(/^false/, TT.FALSE)) {
        continue;
      }
      if (this.lexMatch(/^null/, TT.NULL)) {
        continue;
      }

      if (this.lexDirective()) {
        continue;
      } // :identifier
      if (this.lexClass()) {
        continue;
      } // /identifier
      // If we're immediately after a tilde, capture a locale before identifiers
      if (this.lexLocale()) {
        continue;
      }
      if (this.lexIdentifier()) {
        continue;
      } // identifier

      if (this.lexMatch(/^:/, TT.COLON)) {
        continue;
      }
      if (this.lexMatch(/^,/, TT.COMMA)) {
        continue;
      }
      if (this.lexMatch(/^\[/, TT.LBRACKET)) {
        continue;
      }
      if (this.lexMatch(/^]/, TT.RBRACKET)) {
        continue;
      }
      if (this.lexMatch(/^\(/, TT.LPAREN)) {
        continue;
      }
      if (this.lexMatch(/^\)/, TT.RPAREN)) {
        continue;
      }

      // Braces are special because they have implicit breaks,
      // allowing them to appear on the same line as other tokens.
      if (this.lexMatch(/^{/, TT.LBRACE, true)) {
        continue;
      }
      if (this.lexMatch(/^}/, TT.RBRACE, true)) {
        continue;
      }

      if (this.lexString()) {
        continue;
      }
      if (this.lexTilde()) {
        // Immediately try to bind a locale after a tilde
        this.lexLocale();
        continue;
      }
      if (this.lexArrow()) {
        continue;
      }
      // Fallback: if previous token was a tilde (e.g., across a loop boundary),
      // parse the locale before identifiers/unknowns can consume pieces of it.
      if (this.lexLocale()) {
        continue;
      }
      if (this.lexUnknown()) {
        continue;
      }

      this.error('Unrecognized token', this.pos);

      break;
    }
  }

  private lexNumber(): boolean {
    return this.withSavedPosition(commit => {
      const token = this.consumeMatch(/^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?/);
      if (token) {
        let value: number | undefined;
        try {
          value = JSON.parse(token.text) as number;
          if (typeof value !== 'number') {
            return false;
          }
        } catch {
          return false;
        }
        commit();
        const numberToken = token as unknown as Token<number>;
        numberToken.value = value;
        this.token(numberToken, TT.NUMBER);
        return true;
      }
      return false;
    });
  }

  private lexPrologue(): boolean {
    return this.withContext('Prologue', () => {
      try {
        const prefix = this.expectStringLiteral('%CADET-');
        const tMajor = this.expectInteger();
        const p1 = this.expectStringLiteral('.');
        const tMinor = this.expectInteger();
        const match = combine([prefix, tMajor, p1, tMinor]);
        const version = new SemVer(tMajor.value, tMinor.value);
        this.token(
          new Token(match.text, {
            start: match.start,
            end: match.end,
            value: new Prologue(version)
          }),
          TT.PROLOGUE
        );
      } catch {
        return false;
      }
      return true;
    });
  }

  private lexLineComment(): boolean {
    const lineComment = LineComment.lex(this);
    if (lineComment) {
      this.comment(lineComment);
      return true;
    }
    return false;
  }

  private lexBlockComment(): boolean {
    const blockComment = BlockComment.lex(this);
    if (blockComment) {
      this.comment(blockComment);
      return true;
    }
    return false;
  }

  private lexBreak(): boolean {
    // semi-colon or newline
    const brk = this.consumeMatch(/^[;\n]/);
    if (brk) {
      this.addBreak(brk);
      return true;
    }
    return false;
  }

  private addBreak(token: Token) {
    // Coalesce multiple breaks into a single token
    if (!this.lastTokenIsType(TT.BREAK)) {
      this.token(token, TT.BREAK);
    }
  }

  private lexIdentifier(): boolean {
    // An ECMAScript identifier
    const word = this.consumeMatch(identifierRegex);
    if (word) {
      const token = word as unknown as StringToken;
      token.value = word.text;
      this.token(token, TT.ID);
      return true;
    }
    return false;
  }

  private lexDirective(): boolean {
    const directive = this.consumeMatch(directiveRegex);
    if (!directive) {
      return false;
    }
    const match = directive as unknown as StringToken;
    // Remove the leading colon
    match.value = directive.text.slice(1);
    this.token(match, TT.DIRECTIVE);
    return true;
  }

  private lexClass(): boolean {
    const cls = this.consumeMatch(classRegex);
    if (!cls) {
      return false;
    }
    const token = cls as unknown as StringToken;
    // Remove the leading slash
    token.value = cls.text.slice(1);
    this.token(token, TT.CLASS);
    return true;
  }

  private lexMatch(regex: RegExp, type: TokenType, implicitBreak = false): boolean {
    const token = this.consumeMatch(regex);
    if (token) {
      if (implicitBreak) {
        this.addBreak(token);
      }
      this.token(token, type);
      if (implicitBreak) {
        this.addBreak(token);
      }
      return true;
    }
    return false;
  }

  private lexString(): boolean {
    // A JavaScript-style string literal
    const string = JSONString.lex(this);
    if (string) {
      this.token(string, TT.STRING);
      return true;
    }
    return false;
  }

  private lexArrow(): boolean {
    const match = Arrow.lex(this);
    if (match) {
      this.token(match, TT.ARROW);
      return true;
    }
    return false;
  }

  private lexTilde(): boolean {
    // Only lex a tilde if it's followed by a locale pattern
    // This prevents tildes that are part of arrows from being lexed separately
    const localePattern = /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*/;

    // Check if we're at a tilde
    if (this.match(/^~/)) {
      // Look ahead to see if there's a locale pattern after the tilde
      const afterTilde = this.text.substring(this.pos + 1);

      // Debug logging (remove in production)
      // console.log(`Tilde at pos ${this.pos}: looking ahead at "${afterTilde.substring(0, 10)}"...`);

      if (localePattern.test(afterTilde)) {
        // This looks like a tilde+locale, so lex the tilde
        const t = this.consumeMatch(/^~/);
        if (t) {
          // console.log(`  -> Lexed tilde at pos ${this.pos - 1}`);
          this.token(t, TT.TILDE);
          return true;
        }
      } else {
        // console.log(`  -> Not followed by locale, skipping tilde`);
      }
    }
    return false;
  }

  // Lex a BCP47-like locale immediately following a tilde token
  private lexLocale(): boolean {
    if (!this.lastTokenIsType(TT.TILDE)) {
      return false;
    }
    // Accept a conservative superset; validation happens later
    // Allow hyphens and alphanumerics, e.g., en-US, zh-Hant-TW
    const m = this.consumeMatch(/^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*/);
    if (m) {
      const tok = m as unknown as StringToken;
      tok.value = m.text;
      this.token(tok, TT.LOCALE);
      return true;
    }
    return false;
  }

  private lexUnknown(): boolean {
    // Any sequence of characters that are not whitespace or a terminator
    // Do not include '-' here so hyphens can be part of LOCALE tokens only
    const unknown = this.consumeMatch(/^[^ \t;\n{},()[\]:-]+/);
    if (unknown) {
      this.token(unknown, TT.UNKNOWN);
      return true;
    }
    return false;
  }

  unlex(): string {
    const tokenTexts = this.tokens.map(t => {
      if (t.type === TT.BREAK) {
        return '¶';
      }
      return t.text;
    });
    return tokenTexts.join(' ');
  }
}
